import { useEffect, useMemo, useState, createContext, useContext } from "react";
import { BrowserRouter, Link, Route, Routes, useParams, useNavigate, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowRight, CalendarDays, Check, ChevronLeft, ChevronRight, Clock3, Home, Leaf, Lock, LogOut, MessageCircle, Sparkles, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import "@/App.css";
import { LanguageProvider, useLanguage } from "@/i18n/LanguageProvider";
import { LanguageSwitcher } from "@/i18n/LanguageSwitcher";
import { formatCurrency, formatDateLong, formatDateMedium, formatWeekdayShort, formatMonthShort, formatMonthShortUpper, formatTime, formatNumber } from "@/i18n/format";
import { formatLocalCurrencyBadge } from "@/i18n/currency";
import { usePublicConfig } from "@/hooks/usePublicConfig";
import { ConciergeChat } from "@/components/ConciergeChat";

// Small display-only badge shown wherever the customer is about to pay or has
// just paid, so nobody in Preview mistakes a sandbox transaction for a real one.
// Auto-hides as soon as a live Stripe key is deployed (see /api/config).
function TestModeBadge({ variant = "banner", dataTestid = "test-mode-badge" }) {
  const { t } = useTranslation();
  const { paymentTestMode } = usePublicConfig();
  if (!paymentTestMode) return null;
  const cls = variant === "inline" ? "test-mode-inline" : "test-mode-banner";
  const label = t("payment.testModeBadge", "TEST MODE");
  const copy = t("payment.testModeCopy", "No real payment will be charged.");
  return (
    <span className={cls} data-testid={dataTestid} title={copy} role="status" aria-live="polite">
      {label}
    </span>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);
function useAuth() { return useContext(AuthContext); }

function AuthProvider({ children }) {
  const [owner, setOwner] = useState(null); // null=unknown, false=guest, {...}=logged in
  const refresh = async () => { try { const r = await axios.get(`${API}/owner/me`); setOwner(r.data); return r.data; } catch { setOwner(false); return false; } };
  useEffect(() => { refresh(); }, []);
  const login = async (email, password) => { const r = await axios.post(`${API}/owner/login`, { email, password }); setOwner(r.data); return r.data; };
  const logout = async () => { try { await axios.post(`${API}/owner/logout`); } catch {} setOwner(false); };
  const changePassword = async (currentPassword, newPassword) => { await axios.post(`${API}/owner/change-password`, { currentPassword, newPassword }); await refresh(); };
  return <AuthContext.Provider value={{ owner, login, logout, changePassword, refresh }}>{children}</AuthContext.Provider>;
}

function ProtectedRoute({ children }) {
  const { owner } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  if (owner === null) return <><Header /><main className="app-shell"><section className="assistant-panel"><div className="step-content"><p className="chat-text" data-testid="auth-loading">{t("assistant.loadingSession")}</p></div></section></main></>;
  if (!owner) return <Navigate to="/login" state={{ from: location }} replace />;
  if (owner.mustChangePassword) return <ChangePasswordScreen />;
  return children;
}

const fallbackServices = [
  { id: "standard-clean", duration: "2 hours", price: 99, popular: true },
  { id: "deep-clean", duration: "3.5 hours", price: 149, popular: true },
  { id: "move-in", duration: "5 hours", price: 220, popular: false },
  { id: "eco-green", duration: "2.5 hours", price: 119, popular: false },
];

// Given a duration string like "2 hours", "3.5 hours", "5 hours", produce a
// localised label using i18n plurals + a "half-hour" special case.
function useLocalizedDuration() {
  const { t } = useTranslation();
  return (raw) => {
    if (!raw) return "";
    const m = String(raw).match(/(\d+(?:\.\d+)?)/);
    if (!m) return raw;
    const n = parseFloat(m[1]);
    if (!Number.isFinite(n)) return raw;
    const intPart = Math.trunc(n);
    if (n !== intPart) return t("duration.hoursHalf", { count: intPart });
    return t("duration.hours", { count: intPart });
  };
}

function localizedServiceName(t, service) {
  const key = `services.${service.id}.name`;
  const val = t(key);
  return val === key ? service.name || service.id : val;
}

function localizedServiceDescription(t, service) {
  const key = `services.${service.id}.description`;
  const val = t(key);
  return val === key ? service.description || "" : val;
}

function Header() {
  const auth = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const { scope } = useLanguage();
  const onLogout = async () => { await auth.logout(); nav("/login"); };
  const loggedIn = auth?.owner && auth.owner !== false;
  return (
    <header className="topbar">
      <Link to="/" className="brand" data-testid="brand-home-link">
        <span className="brand-mark"><Sparkles size={17} /></span>
        <span>Bookly<span className="brand-ai">AI</span></span>
      </Link>
      <nav className="topbar-nav">
        <LanguageSwitcher testIdPrefix={scope === "owner" ? "owner-language" : "language"} />
        {loggedIn ? (
          <>
            <Link to="/dashboard" className="dashboard-link" data-testid="dashboard-navigation-link">{t("nav.dashboard")} <ArrowRight size={15} /></Link>
            <button type="button" onClick={onLogout} className="dashboard-link logout-link" data-testid="logout-button"><LogOut size={13} /> {t("nav.signOut")}</button>
          </>
        ) : (
          <Link to="/login" className="dashboard-link" data-testid="owner-login-link">{t("nav.ownerLogin")} <ArrowRight size={15} /></Link>
        )}
      </nav>
    </header>
  );
}

function Progress({ step }) {
  const { t } = useTranslation();
  const items = [
    ["1", "progress.service"],
    ["2", "progress.when"],
    ["3", "progress.details"],
  ];
  return (
    <div className="progress" data-testid="booking-progress">
      {items.map(([n, key], idx) => (
        <span key={n} className={step >= idx + 1 ? "active" : ""}>
          {t("progress.step", { n })} <small>{t(key)}</small>
          {idx < items.length - 1 && <i />}
        </span>
      ))}
    </div>
  );
}

function Assistant() {
  const [services, setServices] = useState(fallbackServices);
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(1);
  const [service, setService] = useState(null);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [form, setForm] = useState({ customerName: "", customerPhone: "", customerEmail: "", address: "", notes: "" });
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { t } = useTranslation();
  const { language } = useLanguage();
  const localizedDuration = useLocalizedDuration();
  const dates = useMemo(() => { const today = new Date(); return Array.from({ length: 14 }, (_, i) => { const d = new Date(today); d.setDate(today.getDate() + i + 1); return d; }); }, []);

  useEffect(() => { axios.get(`${API}/services`).then(r => setServices(r.data)).catch(() => {}); }, []);
  useEffect(() => { axios.get(`${API}/config`).then(r => setConfig(r.data)).catch(() => {}); }, []);
  const chooseDate = async (d) => { const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; setDate(value); setSelectedSlot(""); setError(""); try { const r = await axios.post(`${API}/slots`, { date: value, serviceId: service.id }); setSlots(r.data.slots); } catch (err) { setSlots([]); setError(err.response?.data?.detail || t("chat.calendarNotConnected")); } };
  const [redirectUrl, setRedirectUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const submitBooking = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setRedirectUrl("");
    setCopied(false);
    // Pre-open a blank window SYNCHRONOUSLY inside the click handler so we keep the
    // user-gesture chain intact. Popup blockers and iframe sandboxes only allow
    // `window.open` when it originates from a user gesture; awaiting axios first
    // would break that chain. If the environment blocks popups (win === null),
    // we fall back to the visible "Open Stripe Checkout" button in the interstitial.
    let stripeWin = null;
    try { stripeWin = window.open("about:blank", "_blank"); } catch {}
    // Defensive: trim + lowercase email so autofill-inserted whitespace or mixed-case
    // duplicates don't create parallel pending_payment records or send confirmation
    // emails to a subtly different address than what the customer sees on-screen.
    const normalizedEmail = (form.customerEmail || "").trim().toLowerCase();
    const payload = { ...form, customerEmail: normalizedEmail, serviceId: service.id, serviceName: service.name || localizedServiceName(t, service), date, time: selectedSlot, price: service.price, timeZone: (Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"), customerLocale: language };
    try {
      const r = await axios.post(`${API}/bookings`, payload);
      if (r.data?.checkoutUrl) {
        setRedirectUrl(r.data.checkoutUrl);
        setStep(5);
        // If the pre-opened tab survived popup blocking, point it at Stripe now.
        if (stripeWin && !stripeWin.closed) {
          try { stripeWin.location.href = r.data.checkoutUrl; } catch { try { stripeWin.close(); } catch {} }
        }
        return;
      }
      // Non-payment path (Stripe disabled)
      if (stripeWin) { try { stripeWin.close(); } catch {} }
      setBooking(r.data);
      setStep(4);
    } catch (err) {
      if (stripeWin) { try { stripeWin.close(); } catch {} }
      setError(err.response?.data?.detail || t("form.genericError"));
    } finally {
      setLoading(false);
    }
  };
  const copyCheckoutUrl = async () => {
    try {
      await navigator.clipboard.writeText(redirectUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {}
  };
  const reset = () => { setStep(1); setService(null); setDate(""); setSelectedSlot(""); setBooking(null); setForm({ customerName: "", customerPhone: "", customerEmail: "", address: "", notes: "" }); };

  // Concierge handoff — LLM has agreed a specific {service, date, time} with
  // the customer; pre-fill the wizard state and jump to Step 3 (details form)
  // so they only need to type name/phone/email/address. Availability is
  // re-validated server-side on POST /bookings, so this never bypasses the
  // authoritative check.
  const applyConciergeHandoff = async ({ serviceId, date: hDate, time: hTime }) => {
    const svc = (services || []).find((s) => s.id === serviceId);
    if (!svc || !hDate || !hTime) return;
    setError("");
    setService(svc);
    setDate(hDate);
    setSelectedSlot(hTime);
    // Refresh slot grid so if the customer changes their mind on Step 3 they
    // still see a valid grid for hDate. Failure is non-fatal — booking
    // submission still works because the wizard already knows the chosen slot.
    try {
      const r = await axios.post(`${API}/slots`, { date: hDate, serviceId: svc.id });
      setSlots(r.data?.slots || []);
    } catch {
      setSlots([]);
    }
    setStep(3);
  };

  const serviceName = service ? localizedServiceName(t, service) : "";

  return <><Header /><main className="app-shell">
    <section className="intro">
      <div className="eyebrow"><span className="live-dot" /> {t("assistant.eyebrow")}</div>
      <h1>{t("assistant.heroLine1")}<br /><em>{t("assistant.heroLine2")}</em></h1>
      <p className="intro-copy">{t("assistant.heroCopy")}</p>
      <div className="trust-row">
        <span><Check size={14} /> {t("assistant.trustVetted")}</span>
        <span><Check size={14} /> {t("assistant.trustFlexible")}</span>
      </div>
    </section>
    <section className="assistant-panel">
      <div className="assistant-head">
        <div className="assistant-avatar"><Sparkles size={18} /></div>
        <div>
          <strong data-testid="assistant-title">{t("assistant.title")}</strong>
          <span data-testid="assistant-status"><span className="status-dot" /> {t("assistant.status")}</span>
        </div>
        <div className="secure-note"><Leaf size={15} /> {t("assistant.secure")}</div>
      </div>
      <Progress step={step} />

      {step === 1 && (
        <div className="step-content">
          <div className="message">
            <span className="bot-bubble"><Sparkles size={14} /></span>
            <div>
              <p className="chat-label">{t("chat.label")}</p>
              <p className="chat-text" data-testid="service-prompt">{t("chat.servicePrompt")}</p>
            </div>
          </div>
          <div className="service-grid">
            {services.map(s => (
              <button className={`service-option ${service?.id === s.id ? "chosen" : ""}`} key={s.id} onClick={() => { setService(s); setStep(2); }} data-testid={`service-option-${s.id}`}>
                <span className="service-icon">{s.id === "eco-green" ? <Leaf /> : <Home />}</span>
                <span className="service-info">
                  <strong>{localizedServiceName(t, s)}</strong>
                  <small>
                    {t("duration.fromPrice", { duration: localizedDuration(s.duration), price: formatCurrency(s.price, language) })}
                    {formatLocalCurrencyBadge(s.price, language, config?.businessTimeZone) && (
                      <span className="ccy-badge" data-testid={`ccy-badge-${s.id}`}> · {formatLocalCurrencyBadge(s.price, language, config?.businessTimeZone)}</span>
                    )}
                  </small>
                  <span>{localizedServiceDescription(t, s)}</span>
                </span>
                {s.popular && <b className="popular">{t("chat.popular")}</b>}
                <ChevronRight size={17} className="service-arrow" />
              </button>
            ))}
          </div>
          {formatLocalCurrencyBadge(100, language, config?.businessTimeZone) && (
            <p className="ccy-disclaimer" data-testid="ccy-disclaimer-services">{t("currency.disclaimer")}</p>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="step-content">
          <button className="back-action" onClick={() => setStep(1)} data-testid="back-to-service-button"><ChevronLeft size={15} /> {t("chat.changeService")}</button>
          <div className="message">
            <span className="bot-bubble"><CalendarDays size={14} /></span>
            <div>
              <p className="chat-label">{t("chat.label")}</p>
              <p className="chat-text" data-testid="date-prompt">{t("chat.datePrompt", { service: serviceName.toLowerCase() })}</p>
            </div>
          </div>
          <div className="date-strip" data-testid="date-options">
            {dates.map(d => {
              const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
              return (
                <button key={val} className={date === val ? "date-option chosen" : "date-option"} onClick={() => chooseDate(d)} data-testid={`date-option-${val}`}>
                  <small>{formatWeekdayShort(d, language)}</small>
                  <strong>{formatNumber(d.getDate(), language)}</strong>
                  <span>{formatMonthShort(d, language)}</span>
                </button>
              );
            })}
          </div>
          {error && <p className="calendar-error" data-testid="calendar-availability-error">{error}</p>}
          {date && slots.length > 0 && (
            <div className="slot-block">
              <div className="slot-heading">
                <span>{t("chat.availableTimes")}</span>
                <small><i className="available-key" /> {t("chat.keyAvailable")} <i className="unavailable-key" /> {t("chat.keyBooked")}</small>
              </div>
              <div className="slot-grid">
                {slots.map(slot => (
                  <button disabled={!slot.available} className={`slot ${selectedSlot === slot.time ? "chosen" : ""}`} key={slot.time} onClick={() => { setSelectedSlot(slot.time); setStep(3); }} data-testid={`time-slot-${slot.time.replaceAll(" ", "-")}`}>
                    {formatTime(slot.time, language)}
                    {!slot.available && <span>{t("chat.booked")}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
          {date && slots.length === 0 && !error && <p className="calendar-error" data-testid="no-slots-message">{t("chat.noSlots")}</p>}
        </div>
      )}

      {step === 3 && (
        <form className="step-content details-form" onSubmit={submitBooking}>
          <button type="button" className="back-action" onClick={() => setStep(2)} data-testid="back-to-calendar-button"><ChevronLeft size={15} /> {t("chat.changeTime")}</button>
          <div className="message">
            <span className="bot-bubble"><MessageCircle size={14} /></span>
            <div>
              <p className="chat-label">{t("chat.label")}</p>
              <p className="chat-text" data-testid="details-prompt">{t("chat.detailsPrompt")}</p>
            </div>
          </div>
          <div className="booking-summary" data-testid="booking-summary">
            <strong>{serviceName}</strong>
            <span><CalendarDays size={14} /> {formatDateLong(date, language)} · <Clock3 size={14} /> {formatTime(selectedSlot, language)}</span>
            <b>
              {formatCurrency(service.price, language)}
              {formatLocalCurrencyBadge(service.price, language, config?.businessTimeZone) && (
                <small className="ccy-badge" data-testid="ccy-badge-summary"> {formatLocalCurrencyBadge(service.price, language, config?.businessTimeZone)}</small>
              )}
            </b>
          </div>
          {formatLocalCurrencyBadge(service.price, language, config?.businessTimeZone) && (
            <p className="ccy-disclaimer" data-testid="ccy-disclaimer-summary">{t("currency.disclaimer")}</p>
          )}
          <div style={{ marginTop: "10px" }}><TestModeBadge dataTestid="test-mode-booking" /></div>
          <div className="form-grid">
            {[
              ["customerName", "form.name", "form.namePlaceholder", "text"],
              ["customerPhone", "form.phone", "form.phonePlaceholder", "tel"],
              ["customerEmail", "form.email", "form.emailPlaceholder", "email"],
              ["address", "form.address", "form.addressPlaceholder", "text"],
            ].map(([key, labelK, placeholderK, type]) => {
              // Explicit autoComplete tokens so password managers / browsers map fields
              // correctly (esp. email — prevents autofilling a stale/misspelled value
              // from a totally unrelated site into the customer email input).
              const autoCompleteToken = key === "customerEmail" ? "email" : key === "customerPhone" ? "tel" : key === "customerName" ? "name" : key === "address" ? "street-address" : "on";
              return (
                <label key={key}>{t(labelK)}
                  <input required type={type} placeholder={t(placeholderK)} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} data-testid={`${key}-input`} autoComplete={autoCompleteToken} inputMode={key === "customerEmail" ? "email" : undefined} />
                </label>
              );
            })}
          </div>
          <label>{t("form.notesLabel")}
            <textarea rows="2" placeholder={t("form.notesPlaceholder")} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="booking-notes-input" />
          </label>
          {error && <p className="form-error" data-testid="booking-error">{error}</p>}
          <button className="primary-button" disabled={loading} data-testid="confirm-booking-button">{loading ? t("form.confirming") : t("form.confirmBooking")}<Lock size={15} /><ArrowRight size={17} /></button>
          <p className="pay-note" data-testid="pay-note"><Lock size={11} /> {t("form.payNote")}</p>
          <p className="legal-acknowledge" data-testid="booking-legal-acknowledge">
            {t("legal.acknowledge.prefix")}
            <Link to="/terms" target="_blank" rel="noopener noreferrer" data-testid="ack-terms-link">{t("legal.acknowledge.termsLink")}</Link>
            {t("legal.acknowledge.and")}
            <Link to="/privacy" target="_blank" rel="noopener noreferrer" data-testid="ack-privacy-link">{t("legal.acknowledge.privacyLink")}</Link>
            {t("legal.acknowledge.suffix")}
          </p>
        </form>
      )}

      {step === 5 && (
        <div className="step-content redirect-interstitial" data-testid="stripe-redirect-panel">
          <div className="success-icon"><Lock size={27} /></div>
          <p className="eyebrow">{t("payment.stripeBadge")}</p>
          <h2>{t("form.redirectingTitle")}</h2>
          <p className="confirm-copy">{t("form.redirectingCopy")}</p>
          <div className="loader-dots" aria-hidden="true"><span /><span /><span /></div>
          {redirectUrl && (
            <>
              {/* Native anchor — the browser handles this click as a fresh user gesture,
                  which works even in strict iframe sandboxes that block `window.top.location`
                  assignment from JS. `target="_blank"` opens Stripe in a new tab (most
                  sandbox-permissive); noopener/noreferrer preserves privacy. */}
              <a
                className="primary-button stripe-fallback-link"
                href={redirectUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="stripe-fallback-link"
              >
                <Lock size={15} /> {t("form.openStripe")} <ArrowRight size={17} />
              </a>
              {/* Absolute last resort: show the URL so the user can copy it and paste
                  into a fresh tab if BOTH auto-redirect and the anchor fail. */}
              <div className="stripe-manual-url">
                <span className="stripe-url-label">{t("form.manualUrlLabel")}</span>
                <div className="stripe-url-row">
                  <input readOnly value={redirectUrl} onClick={(e) => e.target.select()} data-testid="stripe-url-input" />
                  <button type="button" onClick={copyCheckoutUrl} data-testid="stripe-url-copy" aria-label={t("form.copyUrl")}>
                    {copied ? <Check size={14} /> : <span className="copy-icon">⧉</span>}
                  </button>
                </div>
              </div>
            </>
          )}
          <p className="chat-hint">{t("form.redirectingHint")}</p>
        </div>
      )}

      {step === 4 && (
        <div className="confirmation step-content">
          <div className="success-icon"><Check size={27} /></div>
          <p className="eyebrow">{t("confirmation.eyebrow")}</p>
          <h2>{t("confirmation.greeting", { name: booking?.customerName?.split(" ")[0] || "" })}</h2>
          <p className="confirm-copy">{t("confirmation.copy", { email: booking?.customerEmail || "" })}</p>
          <div className="confirmation-card" data-testid="booking-confirmation">
            <div><small>{t("confirmation.labelService")}</small><strong>{booking?.serviceName}</strong></div>
            <div><small>{t("confirmation.labelDateTime")}</small><strong>{formatDateMedium(booking?.date, language)} · {formatTime(booking?.time, language)}</strong></div>
            <div><small>{t("confirmation.labelAddress")}</small><strong>{booking?.address}</strong></div>
          </div>
          {booking?.manageToken && <a className="manage-link" href={`/manage/${booking.manageToken}`} data-testid="manage-booking-link">{t("confirmation.manageLink")} <ArrowRight size={14} /></a>}
          <button className="secondary-button" onClick={reset} data-testid="book-another-button">{t("confirmation.bookAnother")} <ArrowRight size={16} /></button>
        </div>
      )}
    </section>
    <ConciergeChat
      enabled={!!config?.conciergeEnabled}
      language={language}
      onHandoff={applyConciergeHandoff}
    />
  </main><SiteFooter /></>;
}

function ManageBooking() {
  const { token } = useParams();
  const [booking, setBooking] = useState(null);
  const [config, setConfig] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [mode, setMode] = useState("view");
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [slots, setSlots] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { language } = useLanguage();
  const dates = useMemo(() => { const d0 = new Date(); return Array.from({ length: 14 }, (_, i) => { const d = new Date(d0); d.setDate(d0.getDate() + i + 1); return d; }); }, []);

  useEffect(() => { axios.get(`${API}/manage/${token}`).then(r => setBooking(r.data)).catch(() => setNotFound(true)); }, [token]);
  useEffect(() => { axios.get(`${API}/config`).then(r => setConfig(r.data)).catch(() => {}); }, []);

  const chooseDate = async (d) => { const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; setDate(value); setSlot(""); setError(""); try { const r = await axios.post(`${API}/slots`, { date: value, serviceId: booking.serviceId }); setSlots(r.data.slots); } catch (err) { const detail = err.response?.data?.detail || ""; setError(/connect google calendar/i.test(detail) ? t("manage.noReschedule") : (detail || t("manage.cannotCheck"))); setSlots([]); } };
  const confirmReschedule = async () => { setLoading(true); setError(""); try { const r = await axios.post(`${API}/manage/${token}/reschedule`, { date, time: slot }); setBooking(r.data); setMode("done"); } catch (err) { setError(err.response?.data?.detail || t("manage.cannotReschedule")); } finally { setLoading(false); } };
  const confirmCancel = async () => { if (!window.confirm(t("manage.confirmCancel"))) return; setLoading(true); setError(""); try { await axios.post(`${API}/manage/${token}/cancel`); setMode("cancelled"); } catch (err) { setError(err.response?.data?.detail || t("manage.cannotCancel")); } finally { setLoading(false); } };

  if (notFound) return <><Header /><main className="app-shell"><section className="assistant-panel" data-testid="manage-not-found"><div className="step-content"><p className="chat-text">{t("manage.notFound")}</p><Link to="/" className="primary-button">{t("manage.backHome")} <ArrowRight size={16} /></Link></div></section></main></>;
  if (!booking) return <><Header /><main className="app-shell"><section className="assistant-panel"><div className="step-content"><p className="chat-text">{t("manage.loading")}</p></div></section></main></>;

  return <><Header /><main className="app-shell manage-shell">
    <section className="assistant-panel" data-testid="manage-panel">
      <div className="assistant-head">
        <div className="assistant-avatar"><Sparkles size={18} /></div>
        <div><strong>{t("manage.title")}</strong><span data-testid="manage-status">{booking.status === "cancelled" ? t("manage.statusCancelled") : t("manage.statusConfirmed")} · #{booking.id.slice(0, 8)}</span></div>
        <div className="secure-note"><Leaf size={15} /> {t("manage.secureLink")}</div>
      </div>
      {mode === "cancelled" ? (
        <div className="confirmation step-content" data-testid="cancelled-view">
          <div className="success-icon"><Check size={27} /></div>
          <p className="eyebrow">{t("manage.eyebrowCancelled")}</p>
          <h2>{t("manage.cancelledTitle")}</h2>
          <p className="confirm-copy">{t("manage.cancelledCopy")}</p>
          <Link to="/" className="secondary-button">{t("manage.bookSomethingNew")} <ArrowRight size={16} /></Link>
        </div>
      ) : mode === "done" ? (
        <div className="confirmation step-content" data-testid="rescheduled-view">
          <div className="success-icon"><Check size={27} /></div>
          <p className="eyebrow">{t("manage.eyebrowRescheduled")}</p>
          <h2>{t("manage.rescheduledTitle")}</h2>
          <div className="confirmation-card">
            <div><small>{t("confirmation.labelService")}</small><strong>{booking.serviceName}</strong></div>
            <div><small>{t("manage.labelNewDateTime")}</small><strong>{formatDateMedium(booking.date, language)} · {formatTime(booking.time, language)}</strong></div>
            <div><small>{t("confirmation.labelAddress")}</small><strong>{booking.address}</strong></div>
          </div>
        </div>
      ) : (
        <div className="step-content">
          <div className="message"><span className="bot-bubble"><Sparkles size={14} /></span><div><p className="chat-label">{t("chat.label")}</p><p className="chat-text" data-testid="manage-intro">{t("manage.intro", { name: booking.customerName.split(" ")[0] })}</p></div></div>
          <div className="booking-summary" data-testid="manage-summary">
            <strong>{booking.serviceName}</strong>
            <span><CalendarDays size={14} /> {formatDateLong(booking.date, language)} · <Clock3 size={14} /> {formatTime(booking.time, language)}</span>
            <b>
              {formatCurrency(booking.price, language)}
              {formatLocalCurrencyBadge(booking.price, language, config?.businessTimeZone) && (
                <small className="ccy-badge" data-testid="ccy-badge-manage"> {formatLocalCurrencyBadge(booking.price, language, config?.businessTimeZone)}</small>
              )}
            </b>
          </div>
          {formatLocalCurrencyBadge(booking.price, language, config?.businessTimeZone) && (
            <p className="ccy-disclaimer" data-testid="ccy-disclaimer-manage">{t("currency.disclaimer")}</p>
          )}
          {mode === "view" && booking.status !== "cancelled" && (
            <div className="manage-actions" data-testid="manage-actions">
              <button className="primary-button" onClick={() => setMode("reschedule")} data-testid="reschedule-button">{t("manage.reschedule")} <ArrowRight size={16} /></button>
              <button className="secondary-button" onClick={confirmCancel} disabled={loading} data-testid="cancel-button">{t("manage.cancel")}</button>
            </div>
          )}
          {mode === "view" && booking.status === "cancelled" && <p className="calendar-error" data-testid="already-cancelled">{t("manage.alreadyCancelled")}</p>}
          {mode === "reschedule" && (
            <>
              <button className="back-action" onClick={() => setMode("view")}><ChevronLeft size={15} /> {t("manage.back")}</button>
              <p className="chat-text">{t("manage.pickNewTime")}</p>
              <div className="date-strip">
                {dates.map(d => {
                  const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                  return (
                    <button key={val} className={date === val ? "date-option chosen" : "date-option"} onClick={() => chooseDate(d)} data-testid={`reschedule-date-${val}`}>
                      <small>{formatWeekdayShort(d, language)}</small>
                      <strong>{formatNumber(d.getDate(), language)}</strong>
                      <span>{formatMonthShort(d, language)}</span>
                    </button>
                  );
                })}
              </div>
              {error && <p className="calendar-error" data-testid="reschedule-error">{error}</p>}
              {date && slots.length > 0 && (
                <div className="slot-block">
                  <div className="slot-heading"><span>{t("chat.availableTimes")}</span></div>
                  <div className="slot-grid">
                    {slots.map(s => (
                      <button key={s.time} disabled={!s.available} className={`slot ${slot === s.time ? "chosen" : ""}`} onClick={() => setSlot(s.time)} data-testid={`reschedule-slot-${s.time.replaceAll(" ", "-")}`}>
                        {formatTime(s.time, language)}
                        {!s.available && <span>{t("chat.booked")}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {date && slots.length === 0 && !error && <p className="calendar-error">{t("chat.noSlots")}</p>}
              {slot && <button className="primary-button" onClick={confirmReschedule} disabled={loading} data-testid="confirm-reschedule-button">{loading ? t("manage.saving") : t("manage.confirmNewTime")} <ArrowRight size={16} /></button>}
            </>
          )}
        </div>
      )}
    </section>
  </main><SiteFooter /></>;
}

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function AvailabilityPanel() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [lastHours, setLastHours] = useState({});
  const { t } = useTranslation();
  useEffect(() => { axios.get(`${API}/owner/settings`).then(r => { setSettings(r.data); const cache = {}; Object.entries(r.data.workingHours || {}).forEach(([k, v]) => { if (v && v.length) cache[k] = v; }); setLastHours(cache); }).catch(() => {}); }, []);
  const updateHours = (day, field, value) => setSettings(s => { const wins = [...((s.workingHours[day]) || [])]; if (wins.length === 0) wins.push({ start: "08:00", end: "18:00" }); wins[0] = { ...wins[0], [field]: value }; setLastHours(prev => ({ ...prev, [day]: wins })); return { ...s, workingHours: { ...s.workingHours, [day]: wins } }; });
  const toggleDay = (day) => setSettings(s => { const current = s.workingHours[day] || []; const active = current.length > 0; if (active) { setLastHours(prev => ({ ...prev, [day]: current })); return { ...s, workingHours: { ...s.workingHours, [day]: [] } }; } const restored = lastHours[day] && lastHours[day].length ? lastHours[day] : [{ start: "08:00", end: "18:00" }]; return { ...s, workingHours: { ...s.workingHours, [day]: restored } }; });
  const addBlocked = (val) => { if (!val || (settings.blockedDates || []).includes(val)) return; setSettings(s => ({ ...s, blockedDates: [...(s.blockedDates || []), val].sort() })); };
  const removeBlocked = (d) => setSettings(s => ({ ...s, blockedDates: (s.blockedDates || []).filter(x => x !== d) }));
  const save = async () => { setSaving(true); setStatus(""); try { const r = await axios.put(`${API}/owner/settings`, settings); setSettings(r.data); setStatus(t("availability.saved")); setTimeout(() => setStatus(""), 2500); } catch { setStatus(t("availability.saveFailed")); } finally { setSaving(false); } };
  if (!settings) return null;
  return (
    <section className="availability-panel" data-testid="availability-panel">
      <div className="availability-heading"><small>{t("availability.eyebrow")}</small><strong>{t("availability.title")}</strong><span>{t("availability.subtitle")}</span></div>
      <div className="availability-grid">
        {DAY_KEYS.map(k => {
          const wins = settings.workingHours[k] || [];
          const active = wins.length > 0;
          return (
            <div className="availability-row" key={k}>
              <label className="availability-toggle">
                <input type="checkbox" checked={active} onChange={() => toggleDay(k)} data-testid={`day-toggle-${k}`} />
                <span>{t(`availability.days.${k}`)}</span>
              </label>
              {active ? (
                <>
                  <input type="time" value={wins[0].start} onChange={e => updateHours(k, "start", e.target.value)} data-testid={`day-start-${k}`} />
                  <span className="availability-sep">{t("availability.to")}</span>
                  <input type="time" value={wins[0].end} onChange={e => updateHours(k, "end", e.target.value)} data-testid={`day-end-${k}`} />
                </>
              ) : (
                <span className="availability-closed">{t("availability.closed")}</span>
              )}
            </div>
          );
        })}
      </div>
      <div className="availability-blocked" data-testid="business-tz-section">
        <small>{t("availability.businessTimezone")}</small>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
          <select
            data-testid="business-tz-select"
            value={settings.businessTimeZone || "UTC"}
            onChange={e => setSettings(s => ({ ...s, businessTimeZone: e.target.value }))}
            style={{ padding: "0.4rem 0.6rem", borderRadius: "6px", border: "1px solid #d0d0d0", minWidth: "260px" }}
          >
            {(() => {
              // Prefer the browser's full IANA list when available (Chrome 100+, Node 20+),
              // else fall back to a curated global list so the dropdown always has content.
              let zones = [];
              try {
                if (typeof Intl.supportedValuesOf === "function") zones = Intl.supportedValuesOf("timeZone");
              } catch (_) {}
              if (!zones || zones.length === 0) {
                zones = [
                  "UTC", "Asia/Riyadh", "Asia/Dubai", "Asia/Karachi", "Asia/Kolkata",
                  "Asia/Singapore", "Asia/Tokyo", "Asia/Shanghai", "Asia/Hong_Kong",
                  "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Madrid",
                  "Europe/Istanbul", "Africa/Cairo", "Africa/Johannesburg",
                  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
                  "America/Sao_Paulo", "America/Mexico_City", "America/Toronto",
                  "Australia/Sydney", "Australia/Melbourne", "Pacific/Auckland",
                ];
              }
              // Ensure the currently-selected value is present even if not in the list
              if (settings.businessTimeZone && !zones.includes(settings.businessTimeZone)) {
                zones = [settings.businessTimeZone, ...zones];
              }
              return zones.map(z => <option key={z} value={z}>{z}</option>);
            })()}
          </select>
          <span style={{ fontSize: "0.75rem", color: "#666" }}>
            {t("availability.businessTimezoneHint")}
          </span>
        </div>
        <div
          data-testid="google-calendar-viewer-tz-tip"
          role="note"
          style={{
            marginTop: "0.6rem",
            padding: "0.6rem 0.75rem",
            borderRadius: "6px",
            background: "#f5f8f6",
            border: "1px solid #dce8e5",
            color: "#173b3a",
            fontSize: "0.78rem",
            lineHeight: 1.45,
          }}
        >
          <strong style={{ color: "#0f766e" }}>
            {t("availability.googleCalendarTipTitle")}
          </strong>
          <div style={{ marginTop: "0.25rem" }}>
            {t("availability.googleCalendarTipBody")}
          </div>
        </div>
      </div>
      <div className="availability-blocked">
        <small>{t("availability.blockedDates")}</small>
        <div className="blocked-chips" data-testid="blocked-chips">
          {(settings.blockedDates || []).map(d => (
            <span key={d} className="blocked-chip" data-testid={`blocked-chip-${d}`}>{d}
              <button type="button" onClick={() => removeBlocked(d)} aria-label={t("availability.removeDate")}><X size={12} /></button>
            </span>
          ))}
          <input type="date" onChange={e => { addBlocked(e.target.value); e.target.value = ""; }} data-testid="blocked-date-input" />
        </div>
      </div>
      <div className="availability-actions">
        <button className="primary-button" onClick={save} disabled={saving} data-testid="save-availability-button">{saving ? t("availability.saving") : t("availability.save")} <Check size={15} /></button>
        {status && <span className="save-status" data-testid="save-status">{status}</span>}
      </div>
    </section>
  );
}

function WeeklyEarnings({ bookings }) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  // Anchor to Monday 00:00 of the current week (ISO week).
  const now = new Date();
  const dayIdx = (now.getDay() + 6) % 7; // 0 = Monday
  const thisMon = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayIdx);
  const WEEKS = 8;
  const weeks = Array.from({ length: WEEKS }, (_, i) => {
    const start = new Date(thisMon); start.setDate(thisMon.getDate() - (WEEKS - 1 - i) * 7);
    const end = new Date(start); end.setDate(start.getDate() + 7);
    return { start, end, total: 0, count: 0 };
  });
  bookings.forEach(b => {
    if (b.paymentStatus !== "paid" || b.status === "cancelled") return;
    // Bucket by when the money actually came in (paidAt if the API exposes it,
    // otherwise createdAt — the booking is created within seconds of the Stripe
    // checkout session, so it's a faithful cash-flow proxy). We deliberately do
    // NOT bucket by service date because that would count future services as
    // "past earnings".
    const ts = b.paidAt || b.createdAt || `${b.date}T12:00:00Z`;
    const bd = new Date(ts);
    if (isNaN(bd.getTime())) return;
    bd.setHours(0, 0, 0, 0);
    for (const w of weeks) { if (bd >= w.start && bd < w.end) { w.total += Number(b.price) || 0; w.count += 1; break; } }
  });
  const thisWeek = weeks[WEEKS - 1];
  const lastWeek = weeks[WEEKS - 2];
  const totalAll = weeks.reduce((s, w) => s + w.total, 0);
  const avg = totalAll / WEEKS;
  const maxTotal = Math.max(...weeks.map(w => w.total), 1);
  const anyData = totalAll > 0;
  const delta = lastWeek.total > 0 ? ((thisWeek.total - lastWeek.total) / lastWeek.total) * 100 : (thisWeek.total > 0 ? 100 : 0);
  const deltaAbs = Math.round(Math.abs(delta));
  const deltaCls = delta > 0 ? "delta-up" : delta < 0 ? "delta-down" : "delta-flat";
  const deltaText = delta > 0 ? `▲ ${deltaAbs}%` : delta < 0 ? `▼ ${deltaAbs}%` : t("dashboard.earnings.deltaFlat");
  // SVG chart geometry
  const chartW = 640, chartH = 170, padX = 12, padY = 22, barGap = 14;
  const barW = (chartW - padX * 2 - barGap * (WEEKS - 1)) / WEEKS;
  return (
    <section className="earnings-panel" data-testid="weekly-earnings-panel">
      <div className="earnings-head">
        <div>
          <p className="eyebrow">{t("dashboard.earnings.eyebrow")}</p>
          <h2>{t("dashboard.earnings.title")}</h2>
          <span>{t("dashboard.earnings.subtitle")}</span>
        </div>
      </div>
      <div className="earnings-summary">
        <div data-testid="earnings-this-week">
          <small>{t("dashboard.earnings.thisWeek")}</small>
          <strong data-testid="earnings-this-week-total">{formatCurrency(thisWeek.total, language)}</strong>
          <span className={deltaCls} data-testid="earnings-delta">{deltaText}</span>
        </div>
        <div data-testid="earnings-last-week">
          <small>{t("dashboard.earnings.lastWeek")}</small>
          <strong data-testid="earnings-last-week-total">{formatCurrency(lastWeek.total, language)}</strong>
          <span>{t("dashboard.earnings.bookings", { count: lastWeek.count })}</span>
        </div>
        <div data-testid="earnings-avg">
          <small>{t("dashboard.earnings.avg")}</small>
          <strong data-testid="earnings-avg-total">{formatCurrency(avg, language)}</strong>
          <span>{t("dashboard.earnings.avgCaption")}</span>
        </div>
      </div>
      {anyData ? (
        <div className="earnings-chart-wrap" data-testid="earnings-chart">
          <svg viewBox={`0 0 ${chartW} ${chartH + 28}`} width="100%" preserveAspectRatio="xMidYMid meet" role="img" aria-label={t("dashboard.earnings.title")}>
            {weeks.map((w, i) => {
              const h = (w.total / maxTotal) * (chartH - padY - 6);
              const x = padX + i * (barW + barGap);
              const y = chartH - h;
              const isCurr = i === WEEKS - 1;
              const monthLabel = formatMonthShort(w.start, language);
              return (
                <g key={i} data-testid={`earnings-bar-${i}`}>
                  <rect x={x} y={y} width={barW} height={h} rx="5" className={isCurr ? "bar-current" : "bar-past"}>
                    <title>{`${monthLabel} ${w.start.getDate()} — ${formatCurrency(w.total, language)} · ${w.count}`}</title>
                  </rect>
                  {w.total > 0 && (
                    <text x={x + barW / 2} y={y - 6} textAnchor="middle" className="bar-value">{formatCurrency(w.total, language)}</text>
                  )}
                  <text x={x + barW / 2} y={chartH + 18} textAnchor="middle" className="bar-label">{monthLabel} {w.start.getDate()}</text>
                </g>
              );
            })}
          </svg>
        </div>
      ) : (
        <div className="earnings-empty" data-testid="earnings-empty">{t("dashboard.earnings.noData")}</div>
      )}
    </section>
  );
}

function BookingDetailModal({ booking, onClose, onCancel, businessTimeZone, t, language, statusLabel, paymentBadge }) {
  // Close on Escape — a11y expectation for modal dialogs. Only bind while open
  // so we don't listen on every keystroke across the whole dashboard.
  useEffect(() => {
    if (!booking) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    // Lock body scroll so mobile touch drags don't scroll the page underneath.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [booking, onClose]);
  const [copied, setCopied] = useState(false);
  if (!booking) return null;
  const pay = paymentBadge ? paymentBadge(booking) : null;
  const displayDate = new Date(`${booking.date}T12:00:00`).toLocaleDateString(language, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  const manageUrl = booking.manageToken ? `${window.location.origin}/manage/${booking.manageToken}` : null;
  const copyManageLink = async () => {
    if (!manageUrl) return;
    try { await navigator.clipboard.writeText(manageUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* noop */ }
  };
  const detailRow = (labelKey, value, testid) => value ? (
    <div className="detail-row" data-testid={testid}>
      <small>{t(labelKey)}</small>
      <strong>{value}</strong>
    </div>
  ) : null;
  return (
    <div className="booking-detail-backdrop" data-testid="booking-detail-backdrop" onClick={onClose} role="presentation">
      <div className="booking-detail-modal" data-testid="booking-detail-modal" role="dialog" aria-modal="true" aria-labelledby="booking-detail-title" onClick={(e) => e.stopPropagation()}>
        <header className="booking-detail-header">
          <div>
            <p className="eyebrow">{t("dashboard.detail.eyebrow", { defaultValue: "Booking details" })}</p>
            <h2 id="booking-detail-title" data-testid="booking-detail-service">{booking.serviceName}</h2>
            <div className="booking-detail-status-row">
              <span className={`booking-status ${booking.status}`} data-testid="booking-detail-status">{statusLabel ? statusLabel(booking.status) : booking.status}</span>
              {pay && <span className={`pay-badge ${pay.cls}`} data-testid="booking-detail-pay">{t(pay.key)}</span>}
            </div>
          </div>
          <button className="booking-detail-close" onClick={onClose} data-testid="booking-detail-close" aria-label={t("dashboard.detail.close", { defaultValue: "Close" })}>×</button>
        </header>
        <section className="booking-detail-body">
          {detailRow("dashboard.detail.labelCustomer", booking.customerName, "detail-customer")}
          {booking.customerPhone && (
            <div className="detail-row" data-testid="detail-phone">
              <small>{t("dashboard.detail.labelPhone", { defaultValue: "Phone" })}</small>
              <strong><a href={`tel:${booking.customerPhone}`} data-testid="detail-phone-link">{booking.customerPhone}</a></strong>
            </div>
          )}
          {booking.customerEmail && (
            <div className="detail-row" data-testid="detail-email">
              <small>{t("dashboard.detail.labelEmail", { defaultValue: "Email" })}</small>
              <strong><a href={`mailto:${booking.customerEmail}`} data-testid="detail-email-link">{booking.customerEmail}</a></strong>
            </div>
          )}
          {detailRow("dashboard.detail.labelAddress", booking.address, "detail-address")}
          {detailRow("dashboard.detail.labelDate", displayDate, "detail-date")}
          {detailRow("dashboard.detail.labelTime", booking.time, "detail-time")}
          {businessTimeZone && detailRow("dashboard.detail.labelTimezone", businessTimeZone, "detail-timezone")}
          {detailRow("dashboard.detail.labelPrice", formatCurrency(booking.price, language), "detail-price")}
          {booking.notes && (
            <div className="detail-row detail-notes" data-testid="detail-notes">
              <small>{t("dashboard.detail.labelNotes", { defaultValue: "Notes" })}</small>
              <strong>{booking.notes}</strong>
            </div>
          )}
          <div className="detail-row detail-id" data-testid="detail-id">
            <small>{t("dashboard.detail.labelId", { defaultValue: "Booking ID" })}</small>
            <code>{booking.id}</code>
          </div>
        </section>
        <footer className="booking-detail-footer">
          {manageUrl && (
            <>
              <a href={manageUrl} target="_blank" rel="noopener noreferrer" className="secondary-button" data-testid="detail-open-manage">
                {t("dashboard.detail.openManage", { defaultValue: "Open customer page (reschedule / cancel)" })}
              </a>
              <button className="secondary-button" onClick={copyManageLink} data-testid="detail-copy-link">
                {copied ? t("dashboard.detail.copied", { defaultValue: "Copied ✓" }) : t("dashboard.detail.copyLink", { defaultValue: "Copy manage link" })}
              </button>
            </>
          )}
          {booking.status !== "cancelled" && (
            <button className="danger-button" onClick={() => onCancel(booking.id)} data-testid="detail-cancel-action">
              {t("dashboard.cancelAction")}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

function Dashboard() {
  const [bookings, setBookings] = useState([]);
  const [calendar, setCalendar] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [calendarError, setCalendarError] = useState("");
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [config, setConfig] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const { t } = useTranslation();
  const { language } = useLanguage();
  useEffect(() => { axios.get(`${API}/config`).then(r => setConfig(r.data)).catch(() => {}); }, []);
  useEffect(() => { const params = new URLSearchParams(window.location.search); const err = params.get("calendar") === "error" && params.get("reason"); if (err) { setCalendarError(decodeURIComponent(err)); window.history.replaceState({}, "", window.location.pathname); } }, []);
  const loadCalendar = async () => {
    try {
      const status = await axios.get(`${API}/calendar/status`);
      setCalendar(status.data.calendar);
      if (status.data.connected) {
        try {
          const list = await axios.get(`${API}/calendar/calendars`);
          setCalendars(list.data);
        } catch (listErr) {
          // Google refresh token expired/revoked → backend now returns 401 with
          // detail "reconnect_required". Treat this as a disconnected state so
          // the UI surfaces a working Connect CTA instead of an empty dropdown.
          if (listErr.response?.status === 401 && listErr.response?.data?.detail === "reconnect_required") {
            setCalendar(null);
            setCalendars([]);
            setCalendarError(t("dashboard.calReconnectRequired"));
            return;
          }
          throw listErr;
        }
      }
    } catch {
      setCalendarError(t("dashboard.calConnectDefaultError"));
    }
  };
  const loadBookings = () => axios.get(`${API}/bookings`).then(r => setBookings(r.data)).catch(() => {});
  useEffect(() => { loadBookings(); loadCalendar(); }, []);
  const connectCalendar = async () => { setLoadingCalendar(true); setCalendarError(""); try { const r = await axios.get(`${API}/calendar/connect`); window.location.assign(r.data.authorizationUrl); } catch (err) { setCalendarError(err.response?.data?.detail || t("dashboard.calConnectFailed")); setLoadingCalendar(false); } };
  const selectCalendar = async (e) => { try { const r = await axios.post(`${API}/calendar/select`, { calendarId: e.target.value }); setCalendar(r.data); } catch { setCalendarError(t("dashboard.calSelectFailed")); } };
  const cancelBooking = async (id) => { if (!window.confirm(t("dashboard.confirmCancelBooking"))) return; try { await axios.patch(`${API}/bookings/${id}/status`, { status: "cancelled" }); await loadBookings(); setSelectedBooking((cur) => cur && cur.id === id ? { ...cur, status: "cancelled" } : cur); } catch { alert(t("dashboard.cancelFailed")); } };
  // Row is fully clickable (mobile-friendly). We swallow clicks originating on
  // inline action buttons/links so their default behaviour (Cancel confirm,
  // open manage link) still fires without also opening the detail modal.
  const openBookingDetail = (b, e) => {
    if (e && e.target && typeof e.target.closest === "function" && e.target.closest("button,a")) return;
    setSelectedBooking(b);
  };
  const todayStr = new Date().toISOString().slice(0, 10);
  const activeBookings = bookings.filter(b => b.status !== "cancelled");
  const upcoming = activeBookings.filter(b => b.date >= todayStr);
  const totalCollected = bookings.filter(b => b.paymentStatus === "paid").reduce((sum, b) => sum + b.price, 0);
  const statusLabel = (s) => {
    const k = `dashboard.status.${s}`;
    const v = t(k);
    return v === k ? s : v;
  };
  const paymentBadge = (b) => {
    if (b.paymentStatus === "paid") return { key: "dashboard.payBadge.paid", cls: "pay-paid" };
    if (b.paymentStatus === "refunded") return { key: "dashboard.payBadge.refunded", cls: "pay-refunded" };
    if (b.paymentStatus === "pending") return { key: "dashboard.payBadge.pending", cls: "pay-pending" };
    if (b.paymentStatus === "failed") return { key: "dashboard.payBadge.failed", cls: "pay-failed" };
    return null;
  };
  return <><Header /><main className="dashboard-shell">
    <div className="dashboard-heading">
      <div>
        <p className="eyebrow">{t("dashboard.eyebrow")}</p>
        <h1>{t("dashboard.title")}</h1>
        <p>{t("dashboard.subtitle")}</p>
        <div style={{ marginTop: "10px" }}><TestModeBadge dataTestid="test-mode-dashboard" /></div>
      </div>
      <Link to="/" className="primary-button" data-testid="new-booking-link">{t("dashboard.newBooking")} <ArrowRight size={16} /></Link>
    </div>
    <section className="calendar-connection" data-testid="calendar-connection-panel">
      <div>
        <small>{t("dashboard.calConnLabel")}</small>
        <strong>{calendar ? t("dashboard.calConnected", { name: calendar.name }) : t("dashboard.calConnect")}</strong>
        <span>{calendar ? t("dashboard.calConnectedCopy") : t("dashboard.calDisconnectedCopy")}</span>
      </div>
      {calendar ? (
        <select value={calendar.id} onChange={selectCalendar} data-testid="calendar-selector">
          {calendars.map(item => <option value={item.id} key={item.id}>{item.name}</option>)}
        </select>
      ) : (
        <button className="primary-button" onClick={connectCalendar} disabled={loadingCalendar} data-testid="connect-google-calendar-button">{loadingCalendar ? t("dashboard.opening") : t("dashboard.calConnect")}</button>
      )}
      {calendarError && <p className="calendar-error" data-testid="calendar-connection-error">{calendarError}</p>}
    </section>
    <AvailabilityPanel />
    <div className="metrics">
      <div data-testid="metric-upcoming"><small>{t("dashboard.metricUpcoming")}</small><strong>{upcoming.length}</strong><span><CalendarDays size={14} /> {t("dashboard.metricUpcomingCaption")}</span></div>
      <div data-testid="metric-revenue"><small>{t("dashboard.metricCollected")}</small><strong>{formatCurrency(totalCollected, language)}</strong><span><Sparkles size={14} /> {t("dashboard.metricCollectedCaption")}</span></div>
      <div data-testid="metric-rating"><small>{t("dashboard.metricRating")}</small><strong>{t("dashboard.metricRatingValue")} <span className="star">★</span></strong><span>{t("dashboard.metricRatingCaption")}</span></div>
    </div>
    <WeeklyEarnings bookings={bookings} />
    <section className="booking-list">
      <div className="list-heading">
        <h2>{t("dashboard.allAppointments")}</h2>
        <span data-testid="booking-count">{t("dashboard.counts", { total: bookings.length, upcoming: upcoming.length })}</span>
      </div>
      {bookings.length ? bookings.map(b => {
        const pay = paymentBadge(b);
        return (
        <article
          className={`booking-row ${b.status === "cancelled" ? "is-cancelled" : ""}`}
          key={b.id}
          data-testid={`booking-row-${b.id}`}
          role="button"
          tabIndex={0}
          aria-label={t("dashboard.detail.openAria", { customer: b.customerName, service: b.serviceName, defaultValue: `View booking details for ${b.customerName}` })}
          onClick={(e) => openBookingDetail(b, e)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedBooking(b); } }}
        >
          <div className="booking-date">
            <strong>{formatNumber(new Date(`${b.date}T12:00:00`).getDate(), language)}</strong>
            <span>{formatMonthShortUpper(new Date(`${b.date}T12:00:00`), language)}</span>
          </div>
          <div className="booking-main"><strong>{b.serviceName}</strong><span>{b.customerName} · {b.address}</span></div>
          <div className="booking-time"><Clock3 size={15} /> {formatTime(b.time, language)}</div>
          <span className={`booking-status ${b.status}`}>{statusLabel(b.status)}</span>
          {pay && <span className={`pay-badge ${pay.cls}`} data-testid={`pay-badge-${b.id}`}>{t(pay.key)}</span>}
          <b className="booking-price">{formatCurrency(b.price, language)}</b>
          <button
            className="row-view-cta"
            onClick={(e) => { e.stopPropagation(); setSelectedBooking(b); }}
            data-testid={`dashboard-view-${b.id}`}
            aria-label={t("dashboard.detail.openAria", { customer: b.customerName, service: b.serviceName, defaultValue: `View booking details for ${b.customerName}` })}
          >
            <span className="row-view-label">{t("dashboard.detail.viewAction", { defaultValue: "View" })}</span>
            <ArrowRight size={14} />
          </button>
        </article>
        );
      }) : <div className="empty-state" data-testid="empty-bookings-state">{t("dashboard.noBookings")}</div>}
    </section>
    <BookingDetailModal
      booking={selectedBooking}
      onClose={() => setSelectedBooking(null)}
      onCancel={cancelBooking}
      businessTimeZone={config?.businessTimeZone}
      t={t}
      language={language}
      statusLabel={statusLabel}
      paymentBadge={paymentBadge}
    />
  </main></>;
}

function SiteFooter() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  return (
    <footer className="site-footer" data-testid="site-footer">
      <span data-testid="site-footer-copyright">{t("legal.footer.copyright", { year })}</span>
      <nav className="site-footer-links" aria-label={t("legal.footer.privacy") + " / " + t("legal.footer.terms")}>
        <Link to="/privacy" data-testid="site-footer-privacy">{t("legal.footer.privacy")}</Link>
        <span className="site-footer-sep" aria-hidden="true">·</span>
        <Link to="/terms" data-testid="site-footer-terms">{t("legal.footer.terms")}</Link>
      </nav>
    </footer>
  );
}

// Shared shell for /privacy and /terms. Two small wrapper components below
// keep each page trivially discoverable at the route level.
function LegalPage({ titleKey, eyebrowKey, placeholderKey, testid }) {
  const { t } = useTranslation();
  return <><Header /><main className="legal-shell" data-testid={testid}>
    <p className="eyebrow" data-testid="legal-eyebrow">{t(eyebrowKey)}</p>
    <h1 data-testid="legal-title">{t(titleKey)}</h1>
    <p className="legal-last-updated" data-testid="legal-last-updated">{t("legal.lastUpdated")}</p>
    <div className="legal-body" data-testid="legal-body">
      <p>{t(placeholderKey)}</p>
    </div>
    <Link to="/" className="secondary-button" data-testid="legal-back-home">{t("legal.back")}</Link>
  </main><SiteFooter /></>;
}

function PrivacyPolicy() {
  return <LegalPage titleKey="legal.privacy.title" eyebrowKey="legal.privacy.eyebrow" placeholderKey="legal.privacy.placeholder" testid="privacy-policy-page" />;
}

function TermsOfService() {
  return <LegalPage titleKey="legal.terms.title" eyebrowKey="legal.terms.eyebrow" placeholderKey="legal.terms.placeholder" testid="terms-of-service-page" />;
}


export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <LanguageProvider>
          <Routes>
            <Route path="/" element={<Assistant />} />
            <Route path="/login" element={<LoginScreen />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/manage/:token" element={<ManageBooking />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
          </Routes>
        </LanguageProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

function LoginScreen() {
  const { owner, login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (owner && owner !== false && !owner.mustChangePassword) nav(loc.state?.from?.pathname || "/dashboard", { replace: true }); if (owner && owner !== false && owner.mustChangePassword) nav("/dashboard", { replace: true }); }, [owner]);
  const submit = async (e) => { e.preventDefault(); setLoading(true); setError(""); try { const info = await login(email, password); nav(info.mustChangePassword ? "/dashboard" : (loc.state?.from?.pathname || "/dashboard"), { replace: true }); } catch (err) { setError(err.response?.data?.detail || t("auth.loginError")); } finally { setLoading(false); } };
  return <><Header /><main className="app-shell auth-shell">
    <section className="assistant-panel auth-panel" data-testid="owner-login-panel">
      <div className="assistant-head">
        <div className="assistant-avatar"><Lock size={18} /></div>
        <div><strong>{t("auth.loginTitle")}</strong><span>{t("auth.loginSubtitle")}</span></div>
        <div className="secure-note"><Leaf size={15} /> {t("auth.encrypted")}</div>
      </div>
      <form className="step-content details-form" onSubmit={submit}>
        <div className="form-grid">
          <label>{t("auth.email")}<input required type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t("auth.emailPlaceholder")} data-testid="owner-email-input" /></label>
          <label>{t("auth.password")}<input required type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} placeholder={t("auth.passwordPlaceholder")} data-testid="owner-password-input" /></label>
        </div>
        {error && <p className="form-error" data-testid="owner-login-error">{error}</p>}
        <button className="primary-button" disabled={loading} data-testid="owner-login-submit">{loading ? t("auth.signingIn") : t("auth.signIn")} <ArrowRight size={16} /></button>
        <p className="auth-hint">{t("auth.loginHint")}</p>
      </form>
    </section>
  </main></>;
}

function ChangePasswordScreen() {
  const { owner, changePassword, logout } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (next !== confirm) { setError(t("auth.mismatchError")); return; }
    if (next.length < 8) { setError(t("auth.lengthError")); return; }
    setLoading(true);
    try { await changePassword(current, next); nav("/dashboard", { replace: true }); }
    catch (err) { setError(err.response?.data?.detail || t("auth.updateError")); }
    finally { setLoading(false); }
  };
  const signOut = async () => { await logout(); nav("/login", { replace: true }); };
  return <><Header /><main className="app-shell auth-shell">
    <section className="assistant-panel auth-panel" data-testid="change-password-panel">
      {owner?.email && (
        <div className="signed-in-badge" data-testid="change-password-signed-in-as">
          <Check size={14} />
          <span>{t("auth.signedInAs")} <strong data-testid="change-password-owner-email">{owner.email}</strong></span>
        </div>
      )}
      <p className="signed-in-banner" data-testid="change-password-signed-in-banner">{t("auth.signedInBanner")}</p>
      <div className="assistant-head">
        <div className="assistant-avatar"><Lock size={18} /></div>
        <div><strong>{t("auth.changeTitle")}</strong><span>{t("auth.changeSubtitle")}</span></div>
        <div className="secure-note"><Leaf size={15} /> {t("auth.required")}</div>
      </div>
      <form className="step-content details-form" onSubmit={submit}>
        <div className="form-grid">
          <label>{t("auth.currentPassword")}<input required type="password" autoComplete="current-password" value={current} onChange={e => setCurrent(e.target.value)} data-testid="current-password-input" /></label>
          <label>{t("auth.newPassword")}<input required type="password" autoComplete="new-password" value={next} onChange={e => setNext(e.target.value)} data-testid="new-password-input" /></label>
          <label>{t("auth.confirmPassword")}<input required type="password" autoComplete="new-password" value={confirm} onChange={e => setConfirm(e.target.value)} data-testid="confirm-password-input" /></label>
        </div>
        {error && <p className="form-error" data-testid="change-password-error">{error}</p>}
        <div className="manage-actions">
          <button className="primary-button" disabled={loading} data-testid="change-password-submit">{loading ? t("auth.savingPassword") : t("auth.updatePassword")} <Check size={15} /></button>
          <button type="button" className="secondary-button" onClick={signOut} data-testid="change-password-signout">{t("auth.signOut")}</button>
        </div>
      </form>
    </section>
  </main></>;
}

// --- Payment success + cancel screens (Stripe redirect targets) ---

function useQueryParam(name) {
  const { search } = useLocation();
  return new URLSearchParams(search).get(name);
}

function PaymentSuccess() {
  const sessionId = useQueryParam("session_id");
  const manageToken = useQueryParam("manageToken");
  const { t } = useTranslation();
  const { language } = useLanguage();
  const nav = useNavigate();
  const [status, setStatus] = useState("polling"); // polling | paid | timeout | error
  const [booking, setBooking] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId && !manageToken) { setStatus("error"); return; }
    let cancelled = false;
    let tries = 0;

    // Fallback path (bypasses /payments/status entirely): if we have a manageToken and
    // the booking is already marked paid in DB (via the Stripe webhook), show success
    // immediately. This makes the confirmation page resilient even when the session
    // polling endpoint is temporarily unreachable (Cloudflare challenge, CDN 403,
    // network blip, cross-tab cookie issue after Stripe redirect, etc).
    const tryManageFallback = async () => {
      if (!manageToken) return false;
      try {
        const b = await axios.get(`${API}/manage/${manageToken}`, { withCredentials: false });
        if (b.data?.paymentStatus === "paid") {
          if (cancelled) return true;
          setBooking(b.data);
          setStatus("paid");
          return true;
        }
      } catch (_) {}
      return false;
    };

    const poll = async () => {
      if (cancelled) return;
      tries += 1;
      setAttempts(tries);
      // Fast path: check the booking record directly on the first attempt — if the
      // webhook has already marked it paid we're done, no need to hit /payments/status.
      if (tries === 1 && await tryManageFallback()) return;
      if (!sessionId) {
        // No session_id in URL — rely purely on the manage-token fallback.
        if (tries >= 20) { setStatus("timeout"); return; }
        setTimeout(poll, tries < 5 ? 1500 : 3000);
        return;
      }
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: false });
        if (r.data?.paymentStatus === "paid") {
          if (cancelled) return;
          setStatus("paid");
          if (manageToken) {
            try {
              const b = await axios.get(`${API}/manage/${manageToken}`, { withCredentials: false });
              if (!cancelled) setBooking(b.data);
            } catch {}
          }
          return;
        }
      } catch (err) {
        // 404 = unknown session id → try the manage-token fallback before giving up.
        // Any other error (401/403/CORS/5xx) also falls through to the fallback so a
        // flaky poll endpoint never blocks a customer from seeing their paid booking.
        if (await tryManageFallback()) return;
        if (err?.response?.status === 404 && !manageToken) { setStatus("error"); return; }
      }
      if (tries >= 20) { setStatus("timeout"); return; }
      setTimeout(poll, tries < 5 ? 1500 : 3000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, manageToken]);

  return <><Header /><main className="app-shell">
    <section className="assistant-panel" data-testid="payment-success-panel">
      <div className="assistant-head">
        <div className="assistant-avatar"><Check size={18} /></div>
        <div>
          <strong>{status === "paid" ? t("payment.successTitle") : t("payment.processingTitle")}</strong>
          <span>{status === "paid" ? t("payment.successStatus") : t("payment.processingStatus")}</span>
        </div>
        <div className="secure-note"><Lock size={15} /> {t("payment.stripeBadge")}</div>
      </div>

      {status === "polling" && (
        <div className="step-content" data-testid="payment-polling">
          <div className="message">
            <span className="bot-bubble"><Sparkles size={14} /></span>
            <div>
              <p className="chat-label">{t("chat.label")}</p>
              <p className="chat-text">{t("payment.processingBody")}</p>
              <p className="chat-hint">{t("payment.processingAttempt", { n: attempts })}</p>
            </div>
          </div>
        </div>
      )}

      {status === "paid" && booking && (
        <div className="confirmation step-content" data-testid="payment-success-view">
          <div className="success-icon"><Check size={27} /></div>
          <p className="eyebrow">{t("confirmation.eyebrow")}</p>
          <h2>{t("confirmation.greeting", { name: booking.customerName?.split(" ")[0] || "" })}</h2>
          <p className="confirm-copy">{t("payment.paidCopy", { email: booking.customerEmail })}</p>
          <div className="confirmation-card">
            <div><small>{t("confirmation.labelService")}</small><strong>{booking.serviceName}</strong></div>
            <div><small>{t("confirmation.labelDateTime")}</small><strong>{formatDateMedium(booking.date, language)} · {formatTime(booking.time, language)}</strong></div>
            <div><small>{t("confirmation.labelAddress")}</small><strong>{booking.address}</strong></div>
            <div><small>{t("payment.amountPaid")}</small><strong>{formatCurrency(booking.price, language)}</strong></div>
          </div>
          {manageToken && <a className="manage-link" href={`/manage/${manageToken}`} data-testid="manage-booking-link">{t("confirmation.manageLink")} <ArrowRight size={14} /></a>}
          <button className="secondary-button" onClick={() => nav("/")} data-testid="book-another-button">{t("confirmation.bookAnother")} <ArrowRight size={16} /></button>
          <div style={{ marginTop: "10px" }}><TestModeBadge dataTestid="test-mode-payment-success" /></div>
        </div>
      )}

      {status === "paid" && !booking && (
        <div className="step-content" data-testid="payment-success-minimal">
          <p className="chat-text">{t("payment.paidNoBooking")}</p>
          <button className="primary-button" onClick={() => nav("/")} data-testid="return-home-button">{t("manage.backHome")} <ArrowRight size={16} /></button>
        </div>
      )}

      {status === "timeout" && (
        <div className="step-content" data-testid="payment-timeout">
          <p className="chat-text">{t("payment.timeout")}</p>
          {manageToken && <a className="primary-button" href={`/manage/${manageToken}`}>{t("confirmation.manageLink")} <ArrowRight size={16} /></a>}
        </div>
      )}

      {status === "error" && (
        <div className="step-content" data-testid="payment-error">
          <p className="calendar-error">{t("payment.linkInvalid")}</p>
          {manageToken && <a className="primary-button" href={`/manage/${manageToken}`} data-testid="manage-booking-link">{t("confirmation.manageLink")} <ArrowRight size={16} /></a>}
          <button className="primary-button" onClick={() => nav("/")}>{t("manage.backHome")} <ArrowRight size={16} /></button>
        </div>
      )}
    </section>
  </main></>;
}

function PaymentCancel() {
  const manageToken = useQueryParam("manageToken");
  const { t } = useTranslation();
  const nav = useNavigate();
  const [cleared, setCleared] = useState(false);

  useEffect(() => {
    // Best-effort: clean up the pending booking so the slot returns to availability.
    if (!manageToken) return;
    axios.post(`${API}/manage/${manageToken}/cancel`).catch(() => {}).finally(() => setCleared(true));
  }, [manageToken]);

  return <><Header /><main className="app-shell">
    <section className="assistant-panel" data-testid="payment-cancel-panel">
      <div className="assistant-head">
        <div className="assistant-avatar"><X size={18} /></div>
        <div>
          <strong>{t("payment.cancelTitle")}</strong>
          <span>{cleared ? t("payment.cancelReleased") : t("payment.cancelReleasing")}</span>
        </div>
      </div>
      <div className="step-content" data-testid="payment-cancel-view">
        <p className="chat-text">{t("payment.cancelBody")}</p>
        <div className="manage-actions">
          <button className="primary-button" onClick={() => nav("/")} data-testid="payment-try-again-button">{t("payment.tryAgain")} <ArrowRight size={16} /></button>
          <button className="secondary-button" onClick={() => nav("/")} data-testid="payment-back-home-button">{t("manage.backHome")}</button>
        </div>
      </div>
    </section>
  </main></>;
}
